(function () {
  const ROOT_SELECTOR = '[data-bundle-card-picker]';

  if (window.BundleProductCardPicker) {
    window.BundleProductCardPicker.init(document);
    return;
  }

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value));
    }

    return String(value).replace(/["\\]/g, '\\$&');
  }

  function parseIdList(value) {
    if (!value) {
      return [];
    }

    return String(value)
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  function intersectIdLists(lists) {
    const validLists = lists.filter((list) => list.length > 0);

    if (validLists.length === 0) {
      return [];
    }

    return validLists.reduce((intersection, list) => {
      return intersection.filter((id) => list.includes(id));
    });
  }

  function normalizeValue(value) {
    return String(value || '').trim().toLowerCase();
  }

  function valuesMatch(left, right) {
    return normalizeValue(left) === normalizeValue(right);
  }

  class BundleProductCardPicker {
    constructor(root) {
      this.root = root;
      this.productForm = root.closest('.product-form');
      this.form = this.productForm
        ? this.productForm.querySelector(
          `form[id="${escapeSelector(root.dataset.productFormId)}"]`,
        )
        : null;
      this.form = this.form || document.getElementById(root.dataset.productFormId);
      this.nativePicker = this.productForm
        ? this.productForm.querySelector('product-variants')
        : null;
      this.buttons = Array.from(
        root.querySelectorAll('.bundle-card-picker__variant-button'),
      );
      this.cards = Array.from(root.querySelectorAll('[data-bundle-product-card]'));
      this.parentData = this.parseJson(root, '[data-bundle-parent-options-json]');
      this.childData = new Map();
      this.refreshQueued = false;
      this.hasPendingLocalSelection = false;
    }

    init() {
      if (!this.form || !this.parentData) {
        console.warn('[Bundle card picker] Parent product form data is missing.');
        return;
      }

      this.cards.forEach((card) => {
        const data = this.parseJson(card, '[data-bundle-child-product-json]');

        if (data) {
          this.childData.set(card, data);
        }
      });

      this.root.dataset.bundleCardPickerInitialized = 'true';
      this.root.addEventListener('click', (event) => this.onClick(event));
      this.form.addEventListener('variant:changed', () => this.queueRefresh());
      this.form.addEventListener('change', (event) => {
        if (event.target.matches('[name="id"], [name^="option"]')) {
          this.queueRefresh();
        }
      });

      this.refresh();
    }

    parseJson(scope, selector) {
      const script = scope.querySelector(selector);

      if (!script) {
        return null;
      }

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.warn('[Bundle card picker] Product JSON could not be parsed.', error);
        return null;
      }
    }

    onClick(event) {
      const button = event.target.closest('.bundle-card-picker__variant-button');

      if (!button || !this.root.contains(button)) {
        return;
      }

      if (button.disabled || button.dataset.soldOut === 'true') {
        return;
      }

      const parentOptionPosition = button.dataset.parentOptionPosition;
      const parentOptionValue = button.dataset.parentOptionValue;

      if (parentOptionPosition && parentOptionValue) {
        const nativeControl = this.findNativeControl(
          parentOptionPosition,
          parentOptionValue,
          button.dataset.parentOptionName,
        );

        if (nativeControl) {
          if (nativeControl.tagName === 'INPUT') {
            nativeControl.checked = true;
          } else {
            nativeControl.value = parentOptionValue;
          }

          nativeControl.dispatchEvent(new Event('change', { bubbles: true }));
          this.queueRefresh();
          return;
        }

        console.warn(
          '[Bundle card picker] Native parent option control was not found.',
          button.dataset.parentOptionName,
          parentOptionValue,
        );
      }

      this.selectLocalButton(button);
      this.hasPendingLocalSelection = true;
      this.queueRefresh();
    }

    selectLocalButton(button) {
      const group = button.closest('[data-bundle-card-option-group]');

      if (!group) {
        return;
      }

      group.querySelectorAll('.bundle-card-picker__variant-button').forEach((groupButton) => {
        groupButton.classList.toggle('is-selected', groupButton === button);
        groupButton.setAttribute('aria-pressed', groupButton === button ? 'true' : 'false');
      });
    }

    findNativeControl(position, value, name) {
      if (!this.nativePicker || !position || !value) {
        return null;
      }

      const escapedPosition = escapeSelector(position);
      const escapedValue = escapeSelector(value);
      const escapedName = escapeSelector(name || '');
      const radio = this.nativePicker.querySelector(
        `input[type="radio"][name="option${escapedPosition}"][value="${escapedValue}"]`,
      );

      if (radio) {
        return radio;
      }

      const select = this.nativePicker.querySelector(
        `select[name="option${escapedPosition}"]`,
      );

      if (select) {
        return select;
      }

      const namedRadio = this.nativePicker.querySelector(
        `input[type="radio"][name="${escapedName}"][value="${escapedValue}"]`,
      );

      if (namedRadio) {
        return namedRadio;
      }

      const namedSelect = this.nativePicker.querySelector(
        `select[name="options[${escapedName}]"], select[data-option-position="${escapedPosition}"]`,
      );

      if (namedSelect) {
        return namedSelect;
      }

      return this.nativePicker.querySelector(
        `input[type="radio"][value="${escapedValue}"]`,
      );
    }

    queueRefresh() {
      if (this.refreshQueued) {
        return;
      }

      this.refreshQueued = true;
      window.requestAnimationFrame(() => {
        this.refreshQueued = false;
        this.refresh();
      });
    }

    refresh() {
      let parentVariant = this.getCurrentParentVariant();
      const hasPendingLocalSelection = this.hasPendingLocalSelection;

      this.hasPendingLocalSelection = false;

      if (!hasPendingLocalSelection) {
        this.syncSelectedButtons(parentVariant);
      }
      this.updateChildAvailability();
      parentVariant = this.syncParentVariantFromCardSelection(parentVariant) || parentVariant;

      if (parentVariant) {
        this.syncSelectedButtons(parentVariant);
      }

      this.updateAddToCartState(parentVariant);
    }

    syncSelectedButtons(parentVariant) {
      this.buttons.forEach((button) => {
        const optionPosition = button.dataset.parentOptionPosition;

        if (!optionPosition || !parentVariant) {
          return;
        }

        const optionIndex = Number(optionPosition) - 1;
        const selected = parentVariant.options[optionIndex] === button.dataset.parentOptionValue;

        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    updateChildAvailability() {
      this.cards.forEach((card) => {
        const childData = this.childData.get(card);

        if (!childData) {
          card.dataset.selectedChildVariantAvailable = 'false';
          return;
        }

        const groups = Array.from(
          card.querySelectorAll('[data-bundle-card-option-group]'),
        );

        groups.forEach((group) => {
          group.querySelectorAll('.bundle-card-picker__variant-button').forEach((button) => {
            this.setButtonAvailability(
              button,
              this.hasAvailableVariantForButton(childData, groups, group, button),
            );
          });
        });

        const selectedChildVariant = this.findSelectedChildVariant(childData, groups);
        this.updateCardImage(card, selectedChildVariant);
        card.dataset.selectedChildVariantAvailable = selectedChildVariant
          && selectedChildVariant.available
          ? 'true'
          : 'false';
      });
    }

    hasAvailableVariantForButton(childData, groups, candidateGroup, candidateButton) {
      const candidateVariantIds = this.getVariantIdsForSelection(
        groups,
        candidateGroup,
        candidateButton,
      );

      return childData.variants.some((variant) => {
        return variant.available && candidateVariantIds.includes(String(variant.id));
      });
    }

    findSelectedChildVariant(childData, groups) {
      const selectedVariantIds = this.getVariantIdsForSelection(groups);

      if (selectedVariantIds.length === 0) {
        return null;
      }

      return childData.variants.find((variant) => {
        return selectedVariantIds.includes(String(variant.id)) && variant.available;
      }) || childData.variants.find((variant) => {
        return selectedVariantIds.includes(String(variant.id));
      });
    }

    getVariantIdsForSelection(groups, candidateGroup, candidateButton) {
      const selectedIdLists = groups.map((group) => {
        const selectedButton = group === candidateGroup
          ? candidateButton
          : group.querySelector('.bundle-card-picker__variant-button.is-selected');

        return selectedButton ? parseIdList(selectedButton.dataset.childVariantIds) : [];
      });

      return intersectIdLists(selectedIdLists);
    }

    getSelectedButtons(card) {
      return Array.from(
        card.querySelectorAll('[data-bundle-card-option-group]'),
      ).map((group) => {
        return group.querySelector('.bundle-card-picker__variant-button.is-selected');
      }).filter(Boolean);
    }

    getSelectedButtonValue(button) {
      return button.dataset.parentOptionValue
        || button.dataset.optionLabel
        || button.textContent;
    }

    syncParentVariantFromCardSelection(currentParentVariant) {
      const matchingVariant = this.findParentVariantFromCardSelection();

      if (!matchingVariant) {
        return null;
      }

      if (
        currentParentVariant
        && String(currentParentVariant.id) === String(matchingVariant.id)
      ) {
        return currentParentVariant;
      }

      this.selectParentVariant(matchingVariant);
      this.syncSelectedButtons(matchingVariant);

      return matchingVariant;
    }

    findParentVariantFromCardSelection() {
      return this.findParentVariantByExplicitVariantIds()
        || this.findParentVariantByExplicitOptions()
        || this.findParentVariantByCardOrder();
    }

    findParentVariantByExplicitVariantIds() {
      const selectedParentVariantIdLists = this.cards.flatMap((card) => {
        return this.getSelectedButtons(card).map((button) => {
          return parseIdList(button.dataset.parentVariantIds);
        });
      });
      const selectedParentVariantIds = intersectIdLists(selectedParentVariantIdLists);

      if (selectedParentVariantIds.length === 0) {
        return null;
      }

      return this.parentData.variants.find((variant) => {
        return selectedParentVariantIds.includes(String(variant.id));
      }) || null;
    }

    findParentVariantByExplicitOptions() {
      const selectedOptions = [];

      this.cards.forEach((card) => {
        this.getSelectedButtons(card).forEach((button) => {
          const position = Number(button.dataset.parentOptionPosition);
          const value = this.getSelectedButtonValue(button);

          if (position > 0 && value) {
            selectedOptions[position - 1] = value;
          }
        });
      });

      const hasSelectedOptions = selectedOptions.some((value) => value);

      if (!hasSelectedOptions) {
        return null;
      }

      return this.parentData.variants.find((variant) => {
        return selectedOptions.every((value, index) => {
          return !value || valuesMatch(variant.options[index], value);
        });
      }) || null;
    }

    findParentVariantByCardOrder() {
      const selectedCardValues = this.cards.map((card) => {
        const selectedButtons = this.getSelectedButtons(card);

        if (selectedButtons.length !== 1) {
          return null;
        }

        return this.getSelectedButtonValue(selectedButtons[0]);
      });

      if (selectedCardValues.some((value) => !value)) {
        return null;
      }

      return this.parentData.variants.find((variant) => {
        if (!variant.options || variant.options.length < selectedCardValues.length) {
          return false;
        }

        return selectedCardValues.every((value, index) => {
          return valuesMatch(variant.options[index], value);
        });
      }) || this.findParentVariantBySelectedValueSet(selectedCardValues);
    }

    findParentVariantBySelectedValueSet(selectedValues) {
      return this.parentData.variants.find((variant) => {
        const unmatchedOptions = variant.options.map((option) => normalizeValue(option));

        return selectedValues.every((value) => {
          const matchIndex = unmatchedOptions.indexOf(normalizeValue(value));

          if (matchIndex === -1) {
            return false;
          }

          unmatchedOptions.splice(matchIndex, 1);
          return true;
        });
      }) || null;
    }

    selectParentVariant(variant) {
      if (!variant) {
        return;
      }

      if (this.nativePicker && typeof this.nativePicker.selectVariant === 'function') {
        this.nativePicker.selectVariant(variant.id);
        return;
      }

      const idInput = this.form.querySelector('[name="id"]');

      if (!idInput) {
        return;
      }

      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.form.dispatchEvent(new CustomEvent('variant:changed', {
        bubbles: true,
        detail: { variant },
      }));
    }

    setButtonAvailability(button, available) {
      button.disabled = !available;
      button.dataset.soldOut = available ? 'false' : 'true';
      button.classList.toggle('is-sold-out', !available);

      if (button.dataset.optionLabel) {
        button.setAttribute(
          'aria-label',
          `${button.dataset.optionLabel}${available ? '' : ' - Sold out'}`,
        );
      }

      const soldOutLabel = button.querySelector('.bundle-card-picker__sold-out-label');

      if (soldOutLabel) {
        soldOutLabel.hidden = available;
      }
    }

    updateCardImage(card, variant) {
      const image = card.querySelector('[data-bundle-card-image]');

      if (!image || !variant || !variant.image || image.src === variant.image) {
        return;
      }

      image.src = variant.image;
    }

    getCurrentParentVariant() {
      const idInput = this.form.querySelector('[name="id"]');

      if (!idInput) {
        return null;
      }

      return this.parentData.variants.find(
        (variant) => String(variant.id) === String(idInput.value),
      );
    }

    updateAddToCartState(parentVariant) {
      const hasInvalidChildSelection = this.cards.some((card) => {
        return card.dataset.selectedChildVariantAvailable !== 'true';
      });
      const shouldDisable = !parentVariant
        || parentVariant.available !== true
        || hasInvalidChildSelection;
      const submitButtons = this.form.querySelectorAll(
        '[data-product-add-to-cart-button], button[name="add"], .product-form__submit',
      );

      submitButtons.forEach((button) => {
        if (shouldDisable) {
          button.disabled = true;
          button.dataset.bundleCardPickerDisabled = 'true';
        } else if (button.dataset.bundleCardPickerDisabled === 'true') {
          button.disabled = false;
          delete button.dataset.bundleCardPickerDisabled;
        }
      });

      if (this.productForm) {
        this.productForm.classList.toggle('has-invalid-bundle-card-picker', shouldDisable);
      }
    }
  }

  function initBundleProductCardPickers(root) {
    root.querySelectorAll(ROOT_SELECTOR).forEach((picker) => {
      if (picker.dataset.bundleCardPickerInitialized === 'true') {
        return;
      }

      new BundleProductCardPicker(picker).init();
    });
  }

  function init(root) {
    if (root.matches && root.matches(ROOT_SELECTOR)) {
      if (root.dataset.bundleCardPickerInitialized !== 'true') {
        new BundleProductCardPicker(root).init();
      }

      return;
    }

    initBundleProductCardPickers(root);
  }

  function observeDynamicPickers() {
    if (!window.MutationObserver || !document.documentElement) {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (
            node.nodeType === 1
            && (
              node.matches(ROOT_SELECTOR)
              || node.querySelector(ROOT_SELECTOR)
            )
          ) {
            init(node);
          }
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  window.BundleProductCardPicker = { init };
  init(document);
  observeDynamicPickers();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
